/**
 * Der Ablauf einer Übungsrunde — rein, ohne Seite und Ton. (06.10.2026, Morse-Überarbeitung)
 *
 * Martin: „Ich kann Geben und Hören noch nicht so ganz nutzen. Start und Stopp geht noch nicht so ganz.“
 * Belegt: keine einzige gebuchte Übung. Die alte Seite spielte beim Hören eine ganze Aufgabe am Stück (25
 * Zeichen, 31 s auf Stufe 1) und wollte sie danach aus dem Gedächtnis getippt haben; beim Geben stand eine
 * Vorlage aus zehn Zeichen, und ausgewertet wurde nur auf Knopfdruck.
 *
 * Jetzt, im Geist von LCWO und den Koch-Trainern:
 *   - Hören: eine Runde aus zehn Gruppen. Gruppe für Gruppe klingt, danach bleibt Zeit zum Tippen; nach jeder
 *     Gruppe die Rückmeldung. Gebucht wird in Bündeln zu fünf Gruppen — jedes Bündel ist EINE Koch-Aufgabe mit
 *     dem neuen Zeichen (shared/morse-koch.ts, `kochAufgabe`). Das Fenster der Stufe (`kochFenster`): die letzten
 *     vier Aufgaben — haben sie zusammen unter 100 Zeichen, zählt es zurück, bis 100 beisammen sind (höchstens 20
 *     Aufgaben); entschieden wird ab 50 Zeichen (`kochStufeNach`). Vier Bündel der Hörrunde sind genau 100.
 *   - Geben: eine Runde aus Zielen (Zeichen, Gruppen oder Wörter). Ausgewertet wird nach einer Pause von
 *     selbst (`gebenEntscheid`).
 *
 * Der Server rechnet die Buchung selbst aus Soll und Ist (memory/morse-store.ts) — `rundeErgebnis` rechnet
 * dieselbe Summe, damit die Zusammenfassung zeigt, was gespeichert wird.
 */
import {
  kochAufgabe,
  kochAuswertung,
  kochStufe,
  kochUebungsZeichen,
  kochZeichen,
  KOCH_ANFAENGER_BIS_STUFE,
  type Auswertung,
  type Guete,
  type KochAufgabe,
  type Umfang,
} from "../../shared/morse-koch.js";
import { grossZeichen, MORSE_TABELLE, punktMs, type DekoderStand } from "../../shared/morse.js";

/* ── Hören ─────────────────────────────────────────────────────────── */

/** So viele Gruppen (oder Wörter) hat eine Hörrunde — bei 20/10 WpM gut anderthalb Minuten. */
export const HOEREN_GRUPPEN = 10;
/** So viele Gruppen bilden eine Koch-Aufgabe (eine Buchungszeile). */
const HOEREN_BUENDEL = 5;
/**
 * Zeit zum Tippen nach dem Ende einer Gruppe: Grundzeit plus je Zeichen.
 * 23.10.2026 (Martin: „Reaktionszeit etwas erhöhen“): rund +40 % (2500 + 800/Zeichen → 3500 + 1100/Zeichen; Anfänger
 * 4000 + 1200 → 5500 + 1700). Wer fertig ist, wartet darauf nicht: Enter wertet sofort, eine voll getippte Gruppe nach `GNADE_MS`.
 */
const EINGABE_GRUND_MS = 3500;
const EINGABE_JE_ZEICHEN_MS = 1100;
/** Wer tippt, bekommt nach jedem Zeichen mindestens so viel Zeit … */
export const NACH_TIPPEN_MS = 2500;
/**
 * … und ist die Gruppe voll getippt, nur noch so viel (zum Verbessern). 23.10.2026 (Martin: „es dauert fast 1 Sekunde bis ein
 * Feedback kommt“): 700 → 400 ms. Wer verbessert, verliert nichts: jede Taste, die die Gruppe wieder kürzer macht, gibt
 * `NACH_TIPPEN_MS` zurück (`fristNachEingabe`).
 */
export const GNADE_MS = 400;
/**
 * Nach dem Sprechen (morse-sprechen.ts) steht das Ergebnis so lange zum Prüfen da, bevor die Gruppe von selbst gewertet wird:
 * Martin soll sehen, was erkannt wurde, und korrigieren können — die „Gnade“ von 0,4 s nach einer voll getippten Gruppe
 * ist dafür zu kurz. Mit leeren (unsicheren) Stellen, die erst noch gewählt werden, mehr.
 */
export const SPRECH_PRUEFEN_MS = 6000;
export const SPRECH_PRUEFEN_UNSICHER_MS = 9000;
/** So lange vor der ersten Gruppe einer Runde (Luft nach dem Tipp auf „Start“). */
export const VORLAUF_MS = 600;
/** So lange zwischen der Rückmeldung und der nächsten Gruppe. */
export const GRUPPEN_LUECKE_MS = 700;

/**
 * Anfänger (bis `KOCH_ANFAENGER_BIS_STUFE`) bekommen beim Hören längere Pausen: mehr Zeit zum Tippen und mehr Luft
 * zwischen Rückmeldung und nächster Gruppe (06.10.2026, Martin: „So ist es mir noch viel zu schwer.“). Nur Zeit —
 * was zählt und wie gebucht wird, bleibt gleich.
 */
export const ANFAENGER_EINGABE_GRUND_MS = 5500;
const ANFAENGER_EINGABE_JE_ZEICHEN_MS = 1700;
const ANFAENGER_GRUPPEN_LUECKE_MS = 1500;

export interface HoerZeiten {
  /** Zeit zum Tippen nach dem Ende einer Gruppe: Grundzeit plus je Zeichen. */
  grundMs: number;
  jeZeichenMs: number;
  /** Zwischen Rückmeldung und nächster Gruppe. */
  lueckeMs: number;
}

/** Die Zeiten einer Hörrunde auf dieser Stufe. */
export function hoerZeiten(stufe: number): HoerZeiten {
  return kochStufe(stufe) <= KOCH_ANFAENGER_BIS_STUFE
    ? { grundMs: ANFAENGER_EINGABE_GRUND_MS, jeZeichenMs: ANFAENGER_EINGABE_JE_ZEICHEN_MS, lueckeMs: ANFAENGER_GRUPPEN_LUECKE_MS }
    : { grundMs: EINGABE_GRUND_MS, jeZeichenMs: EINGABE_JE_ZEICHEN_MS, lueckeMs: GRUPPEN_LUECKE_MS };
}

export interface HoerRunde {
  /** Was wirklich kommt — „woerter“ kann zu „gruppen“ werden, wenn die Stufe kein Wort mit dem neuen Zeichen hat. */
  art: KochAufgabe["art"];
  gruppen: string[];
  /** Zu welchem Bündel (Koch-Aufgabe) jede Gruppe gehört. */
  buendel: number[];
}

/**
 * Eine Hörrunde für diese Stufe — Bündel für Bündel aus `kochAufgabe`, das neue Zeichen in jedem Bündel. Mit
 * `umfang: "alle"` (08.10.2026) kommen die Gruppen aus allen Zeichen des Kurses, gleichmäßig gemischt; das Bündeln bleibt
 * (gebucht wird gleich), nur zählt die Buchung dann nicht für die Stufe (der Server weiß es aus `umfang`).
 */
export function hoerRunde(
  stufe: number,
  o: { art?: KochAufgabe["art"]; guete?: Guete; zufall?: () => number; anzahl?: number; umfang?: Umfang } = {},
): HoerRunde {
  const anzahl = Math.max(1, Math.round(o.anzahl ?? HOEREN_GRUPPEN));
  const gruppen: string[] = [];
  const buendel: number[] = [];
  let art: KochAufgabe["art"] = o.art ?? "gruppen";
  for (let b = 0; gruppen.length < anzahl; b++) {
    const a = kochAufgabe(stufe, { art: o.art, anzahl: Math.min(HOEREN_BUENDEL, anzahl - gruppen.length), guete: o.guete, zufall: o.zufall, umfang: o.umfang });
    if (a.art !== art) art = a.art;
    for (const g of a.text.split(" ")) {
      gruppen.push(g);
      buendel.push(b);
    }
  }
  return { art, gruppen, buendel };
}

/** Nur die Zeichen, die es im Morse gibt, in Großbuchstaben (wie die Auswertung zählt). */
export function zeichenVon(text: string): string[] {
  return [...text.normalize("NFC")].map(grossZeichen).filter((z) => MORSE_TABELLE[z] !== undefined);
}

/** Zeit zum Tippen nach dem Ende einer Gruppe dieser Länge (Vorgabe: die Zeiten ab Stufe 3). */
export function eingabeZeitMs(laenge: number, z: Pick<HoerZeiten, "grundMs" | "jeZeichenMs"> = { grundMs: EINGABE_GRUND_MS, jeZeichenMs: EINGABE_JE_ZEICHEN_MS }): number {
  return z.grundMs + z.jeZeichenMs * Math.max(1, laenge);
}

/**
 * Die neue Frist nach einer Eingabe: voll getippt → nur noch `GNADE_MS`; sonst nie weniger als
 * `NACH_TIPPEN_MS` (wer tippt, wird nicht mitten im Wort abgeschnitten).
 */
export function fristNachEingabe(jetzt: number, frist: number, getippt: number, laenge: number): number {
  if (getippt >= laenge) return jetzt + GNADE_MS;
  return Math.max(frist, jetzt + NACH_TIPPEN_MS);
}

/** Eine beantwortete Gruppe. */
export interface Antwort {
  soll: string;
  ist: string;
  auswertung: Auswertung;
}

/**
 * Die beantworteten Gruppen als Buchung: je Bündel EINE Aufgabe, Gruppen mit Leerzeichen getrennt. Bündel ohne
 * Antwort fallen weg; eine Runde, die mittendrin gestoppt wurde, bucht nur, was beantwortet ist.
 */
export function rundeAlsAufgaben(
  runde: HoerRunde,
  antworten: ReadonlyArray<Antwort | undefined>,
  ohne: ReadonlySet<number> = new Set(),
): Array<{ soll: string; ist: string }> {
  const je = new Map<number, { soll: string[]; ist: string[] }>();
  antworten.forEach((a, i) => {
    if (!a || ohne.has(i)) return;
    const b = runde.buendel[i] ?? 0;
    const x = je.get(b) ?? { soll: [], ist: [] };
    x.soll.push(a.soll);
    x.ist.push(a.ist.trim());
    je.set(b, x);
  });
  return [...je.keys()].sort((a, b) => a - b).map((b) => ({ soll: je.get(b)!.soll.join(" "), ist: je.get(b)!.ist.join(" ") }));
}

/**
 * Was jetzt zu buchen ist (Prüfbefund W4: vorher erst bei Stopp — verwarf iOS die Seite, war alles weg).
 *   - `nurVolle`: nur Bündel, deren Gruppen ALLE beantwortet sind (gleich nach der fünften Gruppe);
 *   - sonst alles Beantwortete, das noch nicht gebucht ist (Stopp, Ende, Seite verdeckt).
 * `gebucht`: Gruppen, die schon in einer Buchung stecken (gesendet oder vorgemerkt) — sie kommen nie zweimal.
 * Gibt die Aufgaben und die Gruppen zurück, die darin stecken.
 */
export function zuBuchen(
  runde: HoerRunde,
  antworten: ReadonlyArray<Antwort | undefined>,
  gebucht: ReadonlySet<number>,
  nurVolle = false,
): { aufgaben: Array<{ soll: string; ist: string }>; gruppen: number[] } {
  const gruppen = runde.gruppen
    .map((_, i) => i)
    .filter((i) => antworten[i] && !gebucht.has(i))
    .filter((i) => !nurVolle || runde.buendel.every((b, j) => b !== runde.buendel[i] || antworten[j] !== undefined));
  const ohne = new Set(runde.gruppen.map((_, i) => i).filter((i) => !gruppen.includes(i)));
  return { aufgaben: rundeAlsAufgaben(runde, antworten, ohne), gruppen };
}

interface RundenErgebnis {
  richtig: number;
  gesamt: number;
  zuviel: number;
  /** richtig / (gesamt + zuviel) — wie der Server und die Liste „Letzte Übungen“ rechnen. */
  quote: number;
}

/** Die Summe über die Aufgaben einer Buchung — dieselbe Rechnung wie memory/morse-store.ts. */
export function rundeErgebnis(aufgaben: ReadonlyArray<{ soll: string; ist: string }>): RundenErgebnis {
  let richtig = 0;
  let gesamt = 0;
  let zuviel = 0;
  for (const a of aufgaben) {
    const x = kochAuswertung(a.soll, a.ist);
    richtig += x.richtig;
    gesamt += x.gesamt;
    zuviel += x.zuviel;
  }
  const nenner = gesamt + zuviel;
  return { richtig, gesamt, zuviel, quote: nenner ? richtig / nenner : 0 };
}

/** Die Zeichen, die in der Runde falsch waren — häufigste zuerst. */
export function unsichereZeichen(auswertungen: ReadonlyArray<Auswertung>): Array<{ zeichen: string; falsch: number }> {
  const zahl = new Map<string, number>();
  for (const a of auswertungen) for (const z of a.zeichen) if (!z.richtig) zahl.set(z.zeichen, (zahl.get(z.zeichen) ?? 0) + 1);
  return [...zahl].map(([zeichen, falsch]) => ({ zeichen, falsch })).sort((a, b) => b.falsch - a.falsch || a.zeichen.localeCompare(b.zeichen));
}

/* ── Geben ─────────────────────────────────────────────────────────── */

export type GebenArt = "zeichen" | "gruppen" | "woerter";
/** So viele Ziele hat eine Geberunde. */
const GEBEN_ZIELE: Readonly<Record<GebenArt, number>> = { zeichen: 10, gruppen: 5, woerter: 5 };
/** So viele Versuche bucht eine Runde höchstens (der Server nimmt 20 Aufgaben je Übung). */
export const GEBEN_VERSUCHE_HOECHSTENS = 20;
/** Ist das Ziel ganz gelesen, wertet die Seite nach so viel Stille aus … */
export const GEBEN_FERTIG_STILLE_MS = 700;
/** … und sonst nach so viel Stille, mit dem, was da ist. */
export const GEBEN_ENDE_STILLE_MS = 2500;
/** So lange ohne Druck, dann pausiert eine Geberunde von selbst — der Ton schläft wieder (Prüfbefund W1). */
export const GEBEN_RUHE_MS = 30_000;
/** Nach einem richtigen Ziel kommt das nächste nach so viel Zeit von selbst. */
export const GEBEN_WEITER_MS = 1100;

/** Die Ziele einer Geberunde. Wörter gibt es nur, wo die Stufe welche mit dem neuen Zeichen hat — sonst Gruppen. */
export function gebenZiele(stufe: number, art: GebenArt, o: { guete?: Guete; zufall?: () => number; umfang?: Umfang } = {}): { art: GebenArt; ziele: string[] } {
  if (art === "zeichen") {
    // Zwei Gruppen aus `kochAufgabe` — gewichtet wie beim Hören, das neue Zeichen ist sicher dabei (mit „alle“: gleichmäßig).
    const ziele = kochAufgabe(stufe, { anzahl: 2, guete: o.guete, zufall: o.zufall, umfang: o.umfang }).text.replace(/ /g, "").split("");
    return { art, ziele: gemischtZuerst(ziele.slice(0, GEBEN_ZIELE.zeichen), stufe, o.umfang) };
  }
  const a = kochAufgabe(stufe, { art: art === "woerter" ? "woerter" : "gruppen", anzahl: GEBEN_ZIELE[art], guete: o.guete, zufall: o.zufall, umfang: o.umfang });
  return { art: a.art === "woerter" ? "woerter" : "gruppen", ziele: gemischtZuerst(a.text.split(" "), stufe, o.umfang) };
}

/** Hat das Zeichen Punkt UND Strich (K, R, U …)? An ihm lernt der Dekoder die Hand. */
export function gemischt(z: string): boolean {
  const m = MORSE_TABELLE[z] ?? "";
  return m.includes(".") && m.includes("-");
}

/**
 * Das erste Ziel einer Runde enthält ein Zeichen mit Punkt UND Strich (Prüfbefund K1).
 *
 * ⚠️ Gemessen am echten Dekoder, 240 Proben je Tempo: bei einem Zeichen nur aus Punkten oder nur aus Strichen
 *    (E, T, S, M) kann KEIN fester Startwert eine Hand von 6 WpM (Punkt 200 ms) und eine von 25 WpM (Strich
 *    144 ms) zugleich richtig lesen — Punkt der einen ist länger als Strich der anderen. Mit Punkt und Strich im
 *    ersten Ziel lernt der Dekoder die Hand am ersten Zeichen (6–25 WpM: 100 %), und sie bleibt für die Runde.
 *    Gibt es auf der Stufe kein solches Ziel, bleibt die Reihenfolge.
 */
function gemischtZuerst(ziele: string[], stufe: number, umfang: Umfang = "meine"): string[] {
  const i = ziele.findIndex((z) => [...z].some(gemischt));
  if (i <= 0) {
    const menge = kochUebungsZeichen(stufe, umfang);
    if (i === 0 || ziele.length === 0 || !menge.some(gemischt) || ziele[0].length > 1) return ziele;
    // Einzelzeichen ohne gemischtes darunter: das erste gemischte der Übungsmenge tritt an die erste Stelle.
    return [menge.find(gemischt)!, ...ziele.slice(1)];
  }
  return [ziele[i], ...ziele.slice(0, i), ...ziele.slice(i + 1)];
}

/** Grenzen einer gemerkten Punktlänge (ms) — 40 WpM bis 3 WpM; alles andere ist Unsinn aus dem Speicher. */
const HAND_MIN_MS = 30;
const HAND_MAX_MS = 400;

/**
 * Womit der Dekoder beim Geben beginnt (Prüfbefund K1): die gelernte Hand der letzten Runde, sonst das
 * geometrische Mittel aus Zeichentempo und wirksamem Tempo (bei 20/10 WpM ≈ 85 ms) — die Mitte zwischen einer
 * schnellen Hand (Zeichentempo) und einer, die im wirksamen Tempo gibt.
 */
export function dekoderStartMs(e: { tempo: number; wirksam: number }, gemerkt?: number | null): number {
  if (typeof gemerkt === "number" && Number.isFinite(gemerkt) && gemerkt >= HAND_MIN_MS && gemerkt <= HAND_MAX_MS) return gemerkt;
  return Math.sqrt(punktMs(e.tempo) * punktMs(e.wirksam));
}

/**
 * Ist ein Versuch zu Ende? `stilleMs`: seit dem letzten Loslassen.
 *   - noch nichts gegeben → weiter warten;
 *   - das Ziel ist ganz gelesen und das letzte Zeichen geschlossen → nach `GEBEN_FERTIG_STILLE_MS`;
 *   - sonst nach `GEBEN_ENDE_STILLE_MS` mit dem, was da ist (ein Anfänger, der hängt, bekommt trotzdem Antwort).
 */
export function gebenEntscheid(ziel: string, s: Pick<DekoderStand, "text" | "offen">, stilleMs: number): "warten" | "fertig" {
  if (!s.text && !s.offen) return "warten";
  if (stilleMs >= GEBEN_ENDE_STILLE_MS) return "fertig";
  const gelesen = [...s.text.replace(/\s/g, "")].length;
  if (!s.offen && gelesen >= zeichenVon(ziel).length && stilleMs >= GEBEN_FERTIG_STILLE_MS) return "fertig";
  return "warten";
}

/* ── Stand ─────────────────────────────────────────────────────────── */

/**
 * Wie sicher die Zeichen der Stufe sitzen: das Mittel ihrer Güte, nie gehörte zählen 0 — `null`, solange
 * keines gehört ist. Ab 0,9 im Fenster der Stufe kommt ein neues Zeichen (Koch).
 */
export function stufenSicherheit(stufe: number, zeichen: ReadonlyArray<{ zeichen: string; guete: number; gehoert: number }>): number | null {
  const je = new Map(zeichen.map((z) => [z.zeichen, z]));
  const liste = kochZeichen(stufe);
  if (!liste.some((z) => (je.get(z)?.gehoert ?? 0) > 0)) return null;
  return liste.reduce((s, z) => s + ((je.get(z)?.gehoert ?? 0) > 0 ? je.get(z)!.guete : 0), 0) / liste.length;
}
